import * as KnowledgeBaseActions from '../actions/knowledge-base.actions';
import {SupportCategoryModel, SupportPostModel} from '@services/back-office';
import {createReducer, on} from '@ngrx/store';
import {TreeHelperService} from '@support/services/tree-helper.service';
import {SupportCategoryCommonModel} from '@support/components/knowledge-base/category-tree/category-tree.component';

const sortCategories = (list: { name?: string; sortOrder?: number }[]) => {
    return list.sort((a, b) => {
        const orderA = a.sortOrder ?? 0;
        const orderB = b.sortOrder ?? 0;

        if (orderA !== orderB) {
            return orderA - orderB;
        }

        return (a.name ?? "").localeCompare(b.name ?? "", "uk");
    });
};

export interface State {
    postsTree: SupportCategoryCommonModel[],
    postsTreeLoading?: boolean,

    postsContent: Record<string, string>,  // відповідність між id статті (поста) і його HTML-контентом
    searchedPosts: SupportPostModel[],
    searchedPostsLoading?: boolean,

    expandedNodeIds?: string[],
    nodeIdsLoading: string[],

    openedNode?: string,
}

export const initialState: State = {
    postsTree: [],
    postsContent: {},
    searchedPosts: [],
    nodeIdsLoading: [],
};

export const reducer = createReducer(
    initialState,

    on(KnowledgeBaseActions.LoadPostsTree, (state) => ({
        ...state,
        postsTreeLoading: true,
    })),

    on(KnowledgeBaseActions.LoadPostsTreeSuccess, (state, action) => ({
        ...state,
        postsTree: action.payload.categories,
        postsTreeLoading: false,
    })),

    on(KnowledgeBaseActions.LoadPostsTreeFailure, (state) => ({
        ...state,
        postsTreeLoading: false,
    })),


    on(KnowledgeBaseActions.AddCategorySuccess, (state, action) => {
        const newCategory = action.payload.response;
        const rootId = action.payload.rootCategoryId;
        const parentId = action.payload.parentCategoryId;

        return {
            ...state,
            expandedNodeIds: [...state.expandedNodeIds ?? [], action.payload.parentCategoryId || action.payload.rootCategoryId],
            postsTree: state.postsTree.map(root => {
                if (root.id !== rootId) return root;

                if (!parentId) {
                    return {
                        ...root,
                        subCategories: sortCategories([
                            ...(root.subCategories ?? []),
                            newCategory
                        ])
                    };
                }

                return {
                    ...root,
                    subCategories: root.subCategories?.map(sub => {
                        if (sub.id === parentId) {
                            return {
                                ...sub,
                                subCategories: sortCategories([
                                    ...((sub as SupportCategoryCommonModel).subCategories ?? []),
                                    newCategory
                                ])
                            };
                        }
                        return sub;
                    }) ?? []
                };
            })
        };
    }),

    on(KnowledgeBaseActions.EditCategorySuccess, (state, action) => {
        const {
            category: updated,
            prevRootCategoryId,
            prevParentCategoryId,
            rootCategoryId,
            parentCategoryId
        } = action.payload;

        function updateAndSort(categories: SupportCategoryCommonModel[], level = 0): SupportCategoryCommonModel[] {
            return categories.map(cat => {
                let updatedCat = { ...cat };
                if (cat.id === updated.id) {
                    updatedCat = { ...updatedCat, ...updated };
                }

                if (updatedCat.subCategories?.length) {
                    updatedCat.subCategories = updateAndSort(updatedCat.subCategories, level + 1);

                    if (level > 0) {
                        updatedCat.subCategories = sortCategories(updatedCat.subCategories);
                    }
                }

                return updatedCat;
            });
        }

        function removeCategory(categories: SupportCategoryCommonModel[], id: string): { updatedTree: SupportCategoryCommonModel[]; removed?: SupportCategoryCommonModel } {
            const result: SupportCategoryCommonModel[] = [];
            let removed: SupportCategoryCommonModel | undefined;

            for (const cat of categories) {
                if (cat.id === id) {
                    removed = { ...cat };
                    continue;
                }

                if (cat.subCategories) {
                    const sub = removeCategory(cat.subCategories, id);
                    if (sub.removed) removed = sub.removed;
                    result.push({ ...cat, subCategories: sub.updatedTree });
                } else {
                    result.push({ ...cat });
                }
            }

            return { updatedTree: result, removed };
        }

        function addCategory(categories: SupportCategoryCommonModel[], parentId: string | undefined, item: SupportCategoryCommonModel): SupportCategoryCommonModel[] {
            if (!parentId) {
                return sortCategories([...categories, item]);
            }

            return categories.map(cat => {
                if (cat.id === parentId) {
                    return {
                        ...cat,
                        subCategories: sortCategories([...(cat.subCategories ?? []), item])
                    };
                }
                return {
                    ...cat,
                    subCategories: cat.subCategories
                        ? addCategory(cat.subCategories, parentId, item)
                        : cat.subCategories
                };
            });
        }

        let newTree: SupportCategoryCommonModel[];

        const movedBetweenRoots = prevRootCategoryId !== rootCategoryId || prevParentCategoryId !== parentCategoryId;

        if (!movedBetweenRoots) {
            newTree = updateAndSort(state.postsTree).map(root => ({
                ...root,
                subCategories: sortCategories(root.subCategories ?? [])
            }));
        } else {
            const { updatedTree, removed } = removeCategory(state.postsTree, updated.id!);
            if (!removed) return state;

            const merged = { ...removed, ...updated };

            newTree = updatedTree.map(root => {
                if (root.id === rootCategoryId) {
                    return {
                        ...root,
                        subCategories: addCategory(root.subCategories ?? [], parentCategoryId, merged)
                    };
                }
                return root;
            });
        }

        return {
            ...state,
            postsTree: newTree
        };
    }),

    on(KnowledgeBaseActions.DeleteCategory, (state, action) => ({
        ...state,
        nodeIdsLoading: [...state.nodeIdsLoading, action.categoryId]
    })),

    on(KnowledgeBaseActions.DeleteCategorySuccess, (state, action) => ({
        ...state,
        postsTree: TreeHelperService.removeNodeFromTree(state.postsTree, action.categoryId),
        expandedNodeIds: state.expandedNodeIds?.filter(id => id !== action.categoryId),
        nodeIdsLoading: state.nodeIdsLoading.filter(id => id !== action.categoryId),
    })),

    on(KnowledgeBaseActions.DeleteCategoryFailure, (state, action) => ({
        ...state,
        nodeIdsLoading: state.nodeIdsLoading.filter(id => id !== action.categoryId),
    })),


    on(KnowledgeBaseActions.SearchPosts, (state) => ({
        ...state,
        searchedPostsLoading: true,
    })),

    on(KnowledgeBaseActions.SearchPostsSuccess, (state, action) => ({
        ...state,
        searchedPosts: action.payload.response,
        searchedPostsLoading: false
    })),

    on(KnowledgeBaseActions.SearchPostsFailure, (state) => ({
        ...state,
        searchedPostsLoading: false
    })),

    on(KnowledgeBaseActions.ClearSearchedPosts, (state) => ({
        ...state,
        searchedPosts: initialState.searchedPosts,
        searchedPostsLoading: initialState.searchedPostsLoading,
    })),


    on(KnowledgeBaseActions.LoadPost, (state, action) => ({
        ...state,
        openedNode: action.payload.id
    })),

    on(KnowledgeBaseActions.LoadPostSuccess, (state, action) => ({
        ...state,
        postsContent: {
            ...state.postsContent,
            [action.payload.response.id!]: action.payload.response.content!,
        }
    })),


    on(KnowledgeBaseActions.UpsertPostSuccess, (state, action) => {
        const post = action.payload.response;
        const cleaned = TreeHelperService.removeNodeFromTree(state.postsTree, post.id!);
        const newTree = TreeHelperService.upsertPostInTree(cleaned, post);

        return {
            ...state,
            openedNode: post.id,
            postsTree: newTree,
            postsContent: {
                ...state.postsContent,
                [post.id!]: post.content!,
            }
        };
    }),


    on(KnowledgeBaseActions.DeletePost, (state, action) => ({
        ...state,
        nodeIdsLoading: [...state.nodeIdsLoading, action.payload.postId]
    })),

    on(KnowledgeBaseActions.DeletePostSuccess, (state, action) => {
        return {
            ...state,
            postsTree: TreeHelperService.removeNodeFromTree(state.postsTree, action.payload.postId),
            nodeIdsLoading: state.nodeIdsLoading.filter(id => id !== action.payload.postId),
        };
    }),

    on(KnowledgeBaseActions.DeletePostFailure, (state, action) => ({
        ...state,
        nodeIdsLoading: state.nodeIdsLoading.filter(id => id !== action.payload.postId),
    })),


    on(KnowledgeBaseActions.TreeToggleNode, (state, action) => {
        const expanded = state.expandedNodeIds?.includes(action.nodeId)
            ? state.expandedNodeIds.filter(id => id !== action.nodeId)
            : [...(state.expandedNodeIds ?? []), action.nodeId];

        return {
            ...state,
            expandedNodeIds: expanded
        };
    }),

    on(KnowledgeBaseActions.TreeOpenNode, (state, action) => {
        return {
            ...state,
            openedNode: action.nodeId,
            ...!state.expandedNodeIds?.includes(action.nodeId) && action.expandNode && {expandedNodeIds: [...(state.expandedNodeIds ?? []), action.nodeId]}
        };
    }),

    on(KnowledgeBaseActions.TreeToggleAllNodes, (state) => {
        const collectIds = (nodes: SupportCategoryCommonModel[]): string[] =>
            nodes.flatMap(node => [
                node.id!,
                ...(node.subCategories ? collectIds(node.subCategories) : [])
            ]);

        const isCollapsed = !state.expandedNodeIds?.length;

        return {
            ...state,
            expandedNodeIds: isCollapsed ? collectIds(state.postsTree ?? []) : []
        };
    }),


    on(KnowledgeBaseActions.ClosePost, (state) => {
        return {
            ...state,
            openedNode: undefined
        };
    }),


    on(KnowledgeBaseActions.SetInitialState, () => ({...initialState}))
);
